import { useAccountContext } from '@/components/providers/account-provider';
import { SocketClient } from '@/services/socket-client';
import { ISocketInitConfig, SocketStatusEnum, Update } from '@/types';
import { generateRandomNonce, KEY_SEPARATOR } from '@/utils/crypto-utils';
import { isNullish } from '@/utils/gun-utils';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { getAgentAddress } from '@/utils/identity-utils/smart-account-agent';
import { WorkerInstance } from '@/utils/worker-utils/worker-instance';
import { useHeadlessEditor } from '@fileverse-dev/ddoc';
import { captureException } from '@sentry/nextjs';
import { fromUint8Array, toUint8Array } from 'js-base64';
import { useCallback, useEffect, useRef, useState } from 'react';
import shortUUID, { uuid } from 'short-uuid';
import * as tweetNaclUtil from 'tweetnacl-util';

export const getUpdateWithNonce = (_update: string) => {
  const [update, nonce] = _update.split(KEY_SEPARATOR);

  return { update, nonce };
};

const useSocketClient = (ddocId: string) => {
  const { portalAddress, editSecret } = useAccountContext();

  const latestPublishedContentHashRef = useRef<string | null>(null);
  const [contentFromSocket, setContentFromSocket] = useState<string[]>([]);

  const [socketClient] = useState<SocketClient>(
    new SocketClient(process.env.NEXT_PUBLIC_WS as string)
  );

  const setLatestPublishedContentHash = (value: string) => {
    latestPublishedContentHashRef.current = value;
  };

  const createUpdateWithNonce = async (update: string) => {
    const nonce = generateRandomNonce();

    const encryptedUpdate = await WorkerInstance.encryptRtcUpdatesViaWorker(
      update,
      getDocRtcKey()!,
      nonce
    );

    return encryptedUpdate + KEY_SEPARATOR + fromUint8Array(nonce);
  };

  const updateIdsTobeDeletedRef = useRef<string[]>([]);

  const selectUpdatesForDeletion = () => {
    updateIdsTobeDeletedRef.current = syncedUpdateIdTrackerRef.current;
  };
  const resetUpdateIdsTobeDeleted = () => {
    updateIdsTobeDeletedRef.current = [];
  };

  const syncedUpdateIdTrackerRef = useRef<string[]>([]);

  const resetSyncedUpdateIdTrackerRef = () => {
    syncedUpdateIdTrackerRef.current = [];
  };

  const pushUpdateIdToSocketTracker = (id: string) => {
    syncedUpdateIdTrackerRef.current = [
      ...syncedUpdateIdTrackerRef.current,
      id,
    ];
  };

  const docOnchainIdRef = useRef<number | null>(null);

  const docOnchainId = () => {
    return docOnchainIdRef.current;
  };
  const setDocOnchainId = (id: number | null) => {
    docOnchainIdRef.current = id;
  };
  const resetDocOnchainId = () => {
    docOnchainIdRef.current = null;
  };

  const deleteUpdatesBeforeLatestIpfsUpload = async (
    fileId: number,
    _ddocId: string
  ) => {
    if (_ddocId !== roomIdRef.current) return;

    const publishedUpdateIds = updateIdsTobeDeletedRef.current;
    if (publishedUpdateIds.length > 0) {
      if (docOnchainId() !== fileId && !isNullish(fileId)) {
        const response = await socketClient.setDocumentFileId(fileId);
        if (!isNullish(response?.data?.on_chain_document_id)) {
          setDocOnchainId(Number(response.data.on_chain_document_id));
        } else {
          captureException(
            'Failed to set doc on chain id on WS or WS returned invalid response'
          );
          return;
        }
      }

      const response = await socketClient.deleteUpdates(
        publishedUpdateIds,
        latestPublishedContentHashRef.current
      );
      if (response?.data?.update_ids) {
        const newSyncedUpdateIds = syncedUpdateIdTrackerRef.current.filter(
          (id) => !response.data.update_ids.includes(id)
        );
        syncedUpdateIdTrackerRef.current = newSyncedUpdateIds;
      }
    }
  };

  const docWsKeyRef = useRef<Uint8Array | null>(null); // used to keep track of ws key
  const setDocRtcKey = async (
    docKey: string,
    _ddocId: string,
    shouldDecrypt = true
  ) => {
    if (_ddocId !== roomIdRef.current) {
      return;
    }
    try {
      if (!docKey) {
        const errMsg = 'Cannot set empty doc key  ' + docKey;
        throw new Error(errMsg);
      }
      const decryptedKey = shouldDecrypt
        ? new TextDecoder().decode(
            await KeyStoreInstance.decryptData(docKey, 'owner')
          )
        : docKey;
      if (!decryptedKey) {
        throw new Error('failed to decrypt rtc key');
      }

      docWsKeyRef.current = tweetNaclUtil.decodeBase64(decryptedKey);
      // process and apply updates that could've been pending due to rtc key missing
      if (updatesFromHistoryRef.current.length > 0) {
        const updates = await decryptUpdatesFromHistory(
          updatesFromHistoryRef.current,
          getDocRtcKey()!
        );
        setContentFromSocket((prev) => [...prev, ...updates]);
        resetUpdatesFromHistory();
      }
      if (
        updateFromPeerYetTobeAppliedOnTheEditorRef.current.length > 0 &&
        !isApplyingUpdateFromPeerRef.current
      ) {
        applyUpdateFromPeer();
      }
      if (unSyncedUpdateQueueRef.current.length > 0) {
        await handleUnsyncedUpdates();
      }
    } catch (error) {
      captureException(error);
    }
  };

  const getDocRtcKey = () => docWsKeyRef.current;

  const resetDocRtcKey = () => {
    docWsKeyRef.current = null;
  };

  const fetchDocContentFromSocket = useCallback(
    async (roomId: string) => {
      if (
        !socketClient ||
        socketClient._webSocketStatus !== SocketStatusEnum.CONNECTED
      ) {
        captureException(
          'Cannot fetch history when socket has not been initialised'
        );
        return [];
      }

      const response = await socketClient.getUncommittedChanges();
      setIsKeyOnWs(!!response?.data?.document_key);
      if (response?.data?.document_key) {
        await setDocRtcKey(response.data.document_key, roomId);
      }

      return response?.data?.history || [];
    },
    [socketClient]
  );

  const updatesFromHistoryRef = useRef<Update[]>([]);

  const pushUpdatesFromHistory = (updates: Update[]) => {
    updatesFromHistoryRef.current = [
      ...updatesFromHistoryRef.current,
      ...updates,
    ];
  };

  const resetUpdatesFromHistory = () => {
    updatesFromHistoryRef.current = [];
  };

  const decryptUpdatesFromHistory = async (
    updatesFromHistory: Update[],
    wsKeyMaterial: Uint8Array
  ) => {
    const { updates, updatesIdList } =
      await WorkerInstance.decryptUpdatesFromHistoryViaWorker(
        updatesFromHistory,
        wsKeyMaterial
      );
    // pushed decrypted update id to tracker
    syncedUpdateIdTrackerRef.current = [
      ...syncedUpdateIdTrackerRef.current,
      ...updatesIdList,
    ];
    return updates;
  };

  const processUpdatesFromSocketHistory = async (roomId: string) => {
    const res = await fetchDocContentFromSocket(roomId);
    const wsKeyMaterial = getDocRtcKey();
    if (!wsKeyMaterial) {
      pushUpdatesFromHistory(res);
      return [];
    }
    return await decryptUpdatesFromHistory(res, wsKeyMaterial);
  };

  const { mergeYjsUpdates } = useHeadlessEditor();

  const unSyncedUpdateQueueRef = useRef<string[]>([]); // Used to hold updates that cannot yet be synced to WS

  const removeSyncedPendingUpdate = (offset: number) => {
    unSyncedUpdateQueueRef.current.splice(0, offset);
  };

  const resetUnsyncedUpdateQueue = () => {
    unSyncedUpdateQueueRef.current = [];
  };

  const handleUnsyncedUpdates = async () => {
    if (
      unSyncedUpdateQueueRef.current.length <= 0 ||
      socketClient._webSocketStatus !== SocketStatusEnum.CONNECTED ||
      !getDocRtcKey()
    )
      return;

    if (!isRtcKeyOnWs()) {
      const encryptedKey = await KeyStoreInstance.encryptData(
        fromUint8Array(getDocRtcKey()!),
        'owner'
      );
      await socketClient.setDocumentKey(encryptedKey);
      setIsKeyOnWs(true);
    }
    const offset = unSyncedUpdateQueueRef.current.length;
    const mergedUpdates = mergeYjsUpdates(unSyncedUpdateQueueRef.current);
    const latestPublishContentHash = latestPublishedContentHashRef.current;
    const update = await createUpdateWithNonce(mergedUpdates);
    const response = await socketClient.sendUpdate({
      data: update,
      last_published_content_hash: latestPublishContentHash || null,
    });
    if (response?.data?.id) {
      pushUpdateIdToSocketTracker(response.data.id);
      if (unSyncedUpdateQueueRef.current.length > 0) {
        removeSyncedPendingUpdate(offset);
      }
    }
  };

  const isKeyOnWsRef = useRef(false);

  const setIsKeyOnWs = (val: boolean) => {
    isKeyOnWsRef.current = val;
  };

  const isRtcKeyOnWs = () => {
    return isKeyOnWsRef.current;
  };

  const onConnect = async ({
    document_key,
    on_chain_document_id,
    roomId,
  }: {
    document_key: string;
    on_chain_document_id: number;
    roomId: string;
  }) => {
    try {
      setIsKeyOnWs(!!document_key);
      if (document_key) {
        setDocRtcKey(document_key, roomId);
      }
      if (!isNullish(on_chain_document_id)) {
        setDocOnchainId(on_chain_document_id);
      }
      const updates = await processUpdatesFromSocketHistory(roomId);
      setContentFromSocket((prev) => [...prev, ...updates]);
      if (unSyncedUpdateQueueRef.current.length > 0 && getDocRtcKey()) {
        await handleUnsyncedUpdates();
      }
    } catch (error) {
      captureException(error);
    }
  };

  const onDisconnect = (event: CloseEvent) => {
    if (event.reason === 'SWITCH ROUTE') return; // we disconnect ws when switching docs.
    captureException(event); // capture reason for disconnection
  };

  const onError = (err: any) => {
    console.log({ err });
    captureException(err);
  };

  const updateFromPeerYetTobeAppliedOnTheEditorRef = useRef<string[]>([]);
  const isApplyingUpdateFromPeerRef = useRef(false);

  const resetUpdateFromPeerYetTobeAppliedOnTheEditor = () => {
    updateFromPeerYetTobeAppliedOnTheEditorRef.current = [];
  };

  const applyUpdateFromPeer = async (latestUpdate?: string) => {
    isApplyingUpdateFromPeerRef.current = true;

    try {
      // Process the current update first
      latestUpdate &&
        updateFromPeerYetTobeAppliedOnTheEditorRef.current.push(latestUpdate);

      while (updateFromPeerYetTobeAppliedOnTheEditorRef.current.length > 0) {
        const _update =
          updateFromPeerYetTobeAppliedOnTheEditorRef.current.shift();
        if (!_update) continue;
        const key = getDocRtcKey()!;
        const { update: updateWithoutNonce, nonce } =
          getUpdateWithNonce(_update);
        const decryptedUpdate = await WorkerInstance.decryptRtcUpdatesViaWorker(
          updateWithoutNonce,
          key,
          toUint8Array(nonce)
        );
        setContentFromSocket((prev) => [...prev, decryptedUpdate]);
      }
    } catch (error) {
      captureException(error);
    } finally {
      isApplyingUpdateFromPeerRef.current = false;
    }
  };

  const processAndApplyUpdate = async (update: string) => {
    if (isApplyingUpdateFromPeerRef.current || !getDocRtcKey()) {
      updateFromPeerYetTobeAppliedOnTheEditorRef.current.push(update);
      return;
    }
    applyUpdateFromPeer(update);
  };

  const removeAppliedUpdates = (offset: number) => {
    setContentFromSocket((prev) => {
      const prevUpdates = [...prev];
      prevUpdates.splice(0, offset);
      return prevUpdates;
    });
  };

  const roomIdRef = useRef('');

  const connectSocket = async (_ddocId: string) => {
    if (
      !socketClient ||
      !getAgentAddress() ||
      !portalAddress ||
      !editSecret ||
      roomIdRef.current !== _ddocId
    ) {
      return;
    }
    try {
      const socketClientConfig: ISocketInitConfig = {
        onConnect,
        onDisconnect,
        onError,
        username: getAgentAddress() as string,
        onWsEvent: (event) => {
          if (event.event_type === 'CONTENT_UPDATE') {
            const update = event.event.data.data;
            processAndApplyUpdate(update);
          }
        },
        roomId: _ddocId,
        didSecret: editSecret,
        portalAddress: portalAddress,
      };
      await socketClient.init(socketClientConfig);
    } catch (error) {
      console.log({ error });
    }
  };

  const { walletAddress } = useAccountContext();

  const isSyncingUpdateRef = useRef<shortUUID.UUID | null>(null); // Used to notify users when sync is in progress and they want to close tab

  const pushUpdateToPendingUpdateQueue = (update: string) => {
    if (!update) return;
    unSyncedUpdateQueueRef.current = [
      ...unSyncedUpdateQueueRef.current,
      update,
    ];
  };
  const walletAddressRef = useRef<string>();
  const storeUpdatesOnSocket = async (update = '') => {
    if (!walletAddressRef.current) return;

    let combinedUpdate = update;
    while (combinedUpdate || unSyncedUpdateQueueRef.current.length > 0) {
      const shouldPendUpdate =
        !socketClient ||
        socketClient._webSocketStatus !== SocketStatusEnum.CONNECTED ||
        isSyncingUpdateRef.current ||
        !getDocRtcKey();

      if (shouldPendUpdate) {
        // pend update ONLY when this user can potentially create or initialise his ID or if we are currently sending an update to WS
        pushUpdateToPendingUpdateQueue(combinedUpdate);
        break;
      }

      const syncId = uuid();
      isSyncingUpdateRef.current = syncId;

      // If WS hasn’t yet received the document key and we have one, send it
      if (!isRtcKeyOnWs() && getDocRtcKey()) {
        const encryptedKey = await KeyStoreInstance.encryptData(
          fromUint8Array(getDocRtcKey()!),
          'owner'
        );
        await socketClient.setDocumentKey(encryptedKey);
        setIsKeyOnWs(true);
      }
      const pendingUpdateCount = unSyncedUpdateQueueRef.current.length;

      // merge any pending updates from the unsynced queue into the current update
      if (unSyncedUpdateQueueRef.current.length > 0) {
        const pendingUpdates = unSyncedUpdateQueueRef.current;
        combinedUpdate = combinedUpdate
          ? mergeYjsUpdates([...pendingUpdates, combinedUpdate])
          : mergeYjsUpdates(pendingUpdates);
      }

      if (!combinedUpdate) break;

      const latestPublishContentHash = latestPublishedContentHashRef.current;

      try {
        const encryptedUpdate = await createUpdateWithNonce(combinedUpdate);

        // send the update on the WS
        const response = await socketClient.sendUpdate({
          data: encryptedUpdate,
          last_published_content_hash: latestPublishContentHash || null,
        });

        if (response?.data?.id) {
          // track the update id for later removal upon publishing
          pushUpdateIdToSocketTracker(response.data.id);
          // remove the updates that have just been synced
          removeSyncedPendingUpdate(pendingUpdateCount);
        } else {
          captureException(
            'Failed to store update on WS or server did not return valid response',
            response
          );
        }
      } catch (error) {
        captureException(error);
        break;
      } finally {
        // Only reset isSyncingUpdateRef if this particular sync attempt is still current
        if (isSyncingUpdateRef.current === syncId) {
          isSyncingUpdateRef.current = null;
        }
      }
      combinedUpdate = '';
    }
  };

  // this is used to make storeUpdatesOnSocket aware of user walletAddress as soon as it is available
  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);

  useEffect(() => {
    roomIdRef.current = ddocId;
  }, [ddocId]);

  return {
    connectSocket,
    fetchDocContentFromSocket,
    storeUpdatesOnSocket,
    socketClient,
    setLatestPublishedContentHash,
    deleteUpdatesBeforeLatestIpfsUpload,
    selectUpdatesForDeletion,
    isSyncingUpdateRef,
    contentFromSocket,
    setContentFromSocket,
    setDocOnchainId,
    removeAppliedUpdates,
    getStateLatestIpfsHash: () => latestPublishedContentHashRef.current,
    resetUpdateIdsTobeDeleted,
    resetSyncedUpdateIdTrackerRef,
    resetDocOnchainId,
    resetDocRtcKey,
    resetUpdatesFromHistory,
    setDocRtcKey,
    getDocRtcKey,
    isRtcKeyOnWs,
    setIsKeyOnWs,
    resetUnsyncedUpdateQueue,
    resetUpdateFromPeerYetTobeAppliedOnTheEditor,
  };
};

export default useSocketClient;
